import { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, MenuItem, Select, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Asset, AssetCategory, AssetStatus, AssetCriticality, Site } from '@cmms/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { primary } from '../../theme/palette'

import { usePermissions } from '../../hooks/usePermissions'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { AssetList } from './components/AssetList'
import { AssetDetail } from './components/AssetDetail'
import { AssetFormDialog } from './components/AssetFormDialog'
import { AssetsImportDialog } from './components/AssetsImportDialog'
import { useBulkSelection } from '../../hooks/useBulkSelection'
import { useConfirm } from '../../components/ConfirmDialog'

export function AssetsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { assetId: urlAssetId } = useParams<{ assetId?: string }>()
  const [selectedId, setSelectedId] = useState<string | null>(urlAssetId ?? null)
  useEffect(() => { if (urlAssetId) setSelectedId(urlAssetId) }, [urlAssetId])
  const [searchParams] = useSearchParams()
  const [search, setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [siteFilter, setSiteFilter] = useState<string>('all')
  // Deep-linkable from the Overview page's "Critical assets" KPI card
  // (?criticality=critical) so clicking the number lands on exactly the
  // assets it counted, instead of a number with no way to see which ones.
  const [criticalityFilter, setCriticalityFilter] = useState<string>(() => searchParams.get('criticality') ?? 'all')
  const [warrantyFilter, setWarrantyFilter] = useState<string>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const { can } = usePermissions()

  // ─── Queries ──────────────────────────────────────────────────
  const assetsQ = useQuery({
    queryKey: ['assets', {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      category_id: categoryFilter !== 'all' ? categoryFilter : undefined,
      site_id: siteFilter !== 'all' ? siteFilter : undefined,
      criticality: criticalityFilter !== 'all' ? criticalityFilter : undefined,
      warranty: warrantyFilter !== 'all' ? warrantyFilter : undefined,
      q: search.trim() || undefined,
    }],
    queryFn: () => api.assets.list({
      status: statusFilter !== 'all' ? statusFilter as AssetStatus : undefined,
      category_id: categoryFilter !== 'all' ? categoryFilter : undefined,
      site_id: siteFilter !== 'all' ? siteFilter : undefined,
      criticality: criticalityFilter !== 'all' ? criticalityFilter as AssetCriticality : undefined,
      warranty: warrantyFilter !== 'all' ? warrantyFilter as 'in_warranty' | 'out_of_warranty' : undefined,
      q: search.trim() || undefined,
    }),
  })

  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories(), staleTime: 5 * 60 * 1000 })
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60 * 1000 })

  const assets: Asset[] = Array.isArray(assetsQ.data) ? assetsQ.data : []
  const categories: AssetCategory[] = Array.isArray(categoriesQ.data) ? categoriesQ.data : []
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Auto-select from URL or first asset
  if (!selectedId && assets.length > 0) {
    const first = assets[0]!.id
    setSelectedId(first)
    navigate(`/assets/${first}`, { replace: true })
  }

  function handleSelect(id: string) {
    setSelectedId(id)
    navigate(`/assets/${id}`, { replace: true })
  }

  const selected = selectedId ? assets.find((a) => a.id === selectedId) ?? null : null

  const confirmDlg = useConfirm()
  const bulk = useBulkSelection()
  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => api.assets.bulkRemove(ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      if (okCount > 0 && skipCount === 0) toast.success(`Deleted ${okCount} asset${okCount === 1 ? '' : 's'}`)
      else if (okCount > 0 && skipCount > 0) toast.success(`Deleted ${okCount}, skipped ${skipCount}`)
      else toast.error('Nothing was deleted')
      qc.invalidateQueries({ queryKey: ['assets'] })
      bulk.clear()
      if (selectedId && res.deleted.includes(selectedId)) setSelectedId(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed'),
  })
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const ok = await confirmDlg({
      title: `Delete ${ids.length} asset${ids.length === 1 ? '' : 's'}?`,
      message: 'They will be soft-deleted and disappear from this list. You can re-import them anytime.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (categoryFilter !== 'all' ? 1 : 0) +
    (siteFilter !== 'all' ? 1 : 0) +
    (criticalityFilter !== 'all' ? 1 : 0) +
    (warrantyFilter !== 'all' ? 1 : 0)

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <TextField placeholder="Search assets" value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: { xs: '100%', md: 300 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
      <Box sx={{ width: 1 }} />
      <FilterChip icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
        label={statusFilter === 'all' ? 'Status' : statusFilter.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        active={statusFilter !== 'all'} value={statusFilter}
        onChange={setStatusFilter}
        options={[{ value: 'all', label: 'All statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' },
          { value: 'under_maintenance', label: 'Under maintenance' }, { value: 'decommissioned', label: 'Decommissioned' }]} />
      <FilterChip icon={<HubOutlinedIcon sx={{ fontSize: 15 }} />}
        label={categoryFilter === 'all' ? 'Category' : (categories.find((c) => c.id === categoryFilter)?.name ?? 'Category')}
        active={categoryFilter !== 'all'} value={categoryFilter}
        onChange={setCategoryFilter}
        options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]} />
      <FilterChip icon={<LocationOnOutlinedIcon sx={{ fontSize: 15 }} />}
        label={siteFilter === 'all' ? 'Site' : (sites.find((s) => s.id === siteFilter)?.name ?? 'Site')}
        active={siteFilter !== 'all'} value={siteFilter}
        onChange={setSiteFilter}
        options={[{ value: 'all', label: 'All sites' }, ...sites.map((s) => ({ value: s.id, label: s.name }))]} />
      <FilterChip icon={<WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />}
        label={criticalityFilter === 'all' ? 'Criticality' : criticalityFilter.replace(/\b\w/g, (c) => c.toUpperCase())}
        active={criticalityFilter !== 'all'} value={criticalityFilter}
        onChange={setCriticalityFilter}
        options={[{ value: 'all', label: 'All criticalities' }, { value: 'critical', label: 'Critical' },
          { value: 'important', label: 'Important' }, { value: 'normal', label: 'Normal' }]} />
      <FilterChip icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 15 }} />}
        label={warrantyFilter === 'all' ? 'Warranty' : warrantyFilter === 'in_warranty' ? 'In warranty' : 'Out of warranty'}
        active={warrantyFilter !== 'all'} value={warrantyFilter}
        onChange={setWarrantyFilter}
        options={[{ value: 'all', label: 'All warranty statuses' }, { value: 'in_warranty', label: 'In warranty' },
          { value: 'out_of_warranty', label: 'Out of warranty' }]} />
      {activeFilterCount > 0 && (
        <Button size="small" onClick={() => { setStatusFilter('all'); setCategoryFilter('all'); setSiteFilter('all'); setCriticalityFilter('all'); setWarrantyFilter('all') }}
          startIcon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: 12, color: 'text.secondary' }}>
          Clear ({activeFilterCount})
        </Button>
      )}
    </Stack>
  )

  return (
    <>
      <PageShell title="Assets" toolbar={toolbar} scrollContent={false}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={can('assets:create') ? '' : "You don't have permission to import assets"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button variant="outlined" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setImportOpen(true)} disabled={!can('assets:create')}
                  sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, borderColor: 'divider', color: 'text.primary' }}>
                  Import CSV
                </Button>
              </Box>
            </Tooltip>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
              <Tooltip title={can('assets:create') ? '' : "You don't have permission to create assets"} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setCreateOpen(true)} disabled={!can('assets:create')}
                    sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                    New asset
                  </Button>
                </Box>
              </Tooltip>
            </motion.div>
          </Stack>
        }>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>
          {assetsQ.isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              <AssetList assets={assets} selectedId={selectedId} onSelect={handleSelect}
                bulk={can('assets:delete') ? bulk : undefined}
                onBulkDelete={handleBulkDelete}
                bulkDeleting={bulkDeleteM.isPending} />
              {selected && <AssetDetail asset={selected} categories={categories} sites={sites} />}
            </>
          )}
        </Box>
      </PageShell>

      {createOpen && <AssetFormDialog mode="create" categories={categories} sites={sites} onClose={() => setCreateOpen(false)} />}
      <AssetsImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        sites={sites}
        categories={categories}
      />
    </>
  )
}

// ─── Filter chip ────────────────────────────────────────────────────────────

export function FilterChip({ icon, label, active, value, onChange, options }: {
  icon: React.ReactNode; label: string; active: boolean; value: string
  onChange: (v: string) => void; options: { value: string; label: string }[]
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} size="small" variant="standard" disableUnderline
      renderValue={() => (
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}>
          <Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'inline-flex' }}>{icon}</Box>
          <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: active ? 'text.primary' : 'text.secondary' }}>{label}</Box>
        </Stack>
      )}
      MenuProps={{ PaperProps: { sx: { minWidth: 200, borderRadius: 1.5, mt: 0.5 } } }}
      sx={{ height: 32, borderRadius: 2, border: '1px solid', borderColor: active ? 'primary.main' : 'divider', bgcolor: 'background.paper',
        '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 }, '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18, right: 6 },
        '&:hover': { borderColor: active ? 'primary.main' : 'text.secondary' } }}>
      {options.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
    </Select>
  )
}
